import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import Header from "../../components/hotels/Header";
import { config } from "../../config";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  calculateFinalPrice,
  formatPrice,
  calculateMarkup,
} from "../../utils/pricing";

function HotelsPaymentScreen() {
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  // const [clientSecret, setClientSecret] = useState(null);
  // const [loading, setLoading] = useState(false);
  // eslint-disable-next-line
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { quote, room } = location.state || {};
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    phone_number: "",
    given_name: "",
    family_name: "",
    special_requests: "",
  });
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  // const [bookingData, setBookingData] = useState(null);

  // Add a console log to show price breakdown
  useEffect(() => {
    if (quote) {
      const originalAmount = parseFloat(quote.total_amount);
      const finalAmount = calculateFinalPrice(originalAmount, true);
      const profit = finalAmount - originalAmount;

      console.log("💰 Hotel Price Breakdown:", {
        duffelPrice: `${quote.total_currency} ${originalAmount.toFixed(2)}`,
        customerPrice: `${quote.total_currency} ${finalAmount.toFixed(2)}`,
        profit: `${quote.total_currency} ${profit.toFixed(2)}`,
        markup:
          config.hotelPricing.markupType === "percentage"
            ? `${config.hotelPricing.markupValue}%`
            : `${config.hotelPricing.markupValue} ${quote.total_currency}`,
      });
    }
  }, [quote]);

  // Redirect if no data
  if (!quote || !room) {
    return <Navigate to="/hotels" replace />;
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      // 1. Create Payment Intent with marked-up amount
      const paymentIntentResponse = await fetch(
        `${config.backendServer}/api/create-payment-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: Math.round(
              calculateFinalPrice(quote.total_amount, true) * 100
            ),
            currency: quote.total_currency.toLowerCase(),
          }),
        }
      );

      const { clientSecret } = await paymentIntentResponse.json();

      // 2. Confirm Card Payment
      const { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: `${formData.given_name} ${formData.family_name}`,
              email: formData.email,
              phone: formData.phone_number,
            },
          },
        });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // 3. Create Booking
      const bookingResponse = await fetch(
        `${config.backendServer}/api/stays/bookings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quote_id: quote.id,
            email: formData.email,
            phone_number: formData.phone_number,
            guests: [
              {
                given_name: formData.given_name,
                family_name: formData.family_name,
              },
            ],
            payment_intent_id: paymentIntent.id,
          }),
        }
      );

      const bookingData = await bookingResponse.json();
      console.log("DuffelBooking Response", bookingData);

      // 4. Send confirmation email
      await fetch(`${config.backendServer}/api/hotel-confirmation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reference: bookingData.data.reference,
          accommodation: bookingData.data.accommodation,
          check_in_date: bookingData.data.check_in_date,
          check_out_date: bookingData.data.check_out_date,
          email: formData.email,
          phone_number: formData.phone_number,
          guests: [
            {
              given_name: formData.given_name,
              family_name: formData.family_name,
            },
          ],
          finalPaidAmount: calculateFinalPrice(
            quote.total_amount,
            true
          ).toFixed(2),
          currency: quote.total_currency,
        }),
      });
      console.log("✉️ Confirmation email sent successfully");

      // 5. Navigate to confirmation page
      navigate("/hotels/confirmation", {
        state: {
          booking: bookingData,
          fromBookingSummary: true,
        },
      });
    } catch (error) {
      console.error("Payment/Booking Error:", error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex === room.photos.length - 1 ? 0 : prevIndex + 1
    );
  };

  const previousPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      prevIndex === 0 ? room.photos.length - 1 : prevIndex - 1
    );
  };

  const calculateTotalFees = () => {
    if (!quote) return "0.00";
    const originalFee = parseFloat(quote.fee_amount || 0);
    const markupAmount = calculateMarkup(quote.total_amount, true);
    const totalFees = originalFee + markupAmount;
    return totalFees.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left side - Booking Form */}
          <div className="md:w-2/3">
            <h1 className="text-2xl font-bold mb-6">Complete your booking</h1>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">
                  Guest Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="given_name"
                      value={formData.given_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="family_name"
                      value={formData.family_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      required
                      placeholder="+1234567890"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6 mt-4">
                <h2 className="text-xl font-semibold mb-4">
                  Payment Information
                </h2>
                <div className="mb-4">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#424770",
                          "::placeholder": {
                            color: "#aab7c4",
                          },
                        },
                        invalid: {
                          color: "#9e2146",
                        },
                      },
                    }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing || !stripe}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              >
                {isProcessing
                  ? "Processing..."
                  : `Pay ${quote.total_currency} ${formatPrice(
                      calculateFinalPrice(quote.total_amount, true),
                      quote.total_currency
                    )}`}
              </button>
            </form>
          </div>

          {/* Right side - Booking Summary */}
          <div className="md:w-1/3">
            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
              <h2 className="text-xl font-semibold mb-4">Booking Summary</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium">{quote.accommodation.name}</h3>
                  <p className="text-sm text-gray-600">{room.name}</p>
                </div>

                {/* Photo Carousel */}
                {room.photos && room.photos.length > 0 && (
                  <div className="relative">
                    <img
                      src={room.photos[currentPhotoIndex].url}
                      alt={`Room view ${currentPhotoIndex + 1}`}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    {room.photos.length > 1 && (
                      <>
                        <button
                          onClick={previousPhoto}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
                          aria-label="Previous photo"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={nextPhoto}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
                          aria-label="Next photo"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
                          {currentPhotoIndex + 1} / {room.photos.length}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex justify-between text-sm">
                    <span>Check-in</span>
                    <span>
                      {new Date(quote.check_in_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span>Check-out</span>
                    <span>
                      {new Date(quote.check_out_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-sm">
                    <span>Base rate</span>
                    <span>
                      {quote.base_currency} {quote.base_amount}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span>Taxes</span>
                    <span>
                      {quote.tax_currency} {quote.tax_amount}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span>Fees</span>
                    <span>
                      {quote.fee_currency} {calculateTotalFees()}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mt-4 border-t pt-4">
                    <span>Total to pay now</span>
                    <span>
                      {/* {quote.total_currency}{" "} */}
                      {formatPrice(
                        calculateFinalPrice(quote.total_amount, true),
                        quote.total_currency
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-4 bg-blue-50 p-3 rounded-md">
                    <span className="font-medium text-blue-800">
                      Due at property
                    </span>
                    <span className="font-medium text-blue-800">
                      {quote.due_at_accommodation_currency}{" "}
                      {quote.due_at_accommodation_amount}
                    </span>
                  </div>
                </div>

                {/* Cancellation Policy */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-2">Cancellation Policy</h3>
                  {quote.accommodation.rooms[0].rates[0].cancellation_timeline.map(
                    (policy, index) => (
                      <p key={index} className="text-sm text-gray-600">
                        Cancel before{" "}
                        {new Date(policy.before).toLocaleDateString()} for a
                        refund of {policy.currency} {policy.refund_amount}
                      </p>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HotelsPaymentScreen;
